<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Conditional Access"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      :showSearch="false"
      :showExport="true"
      :exportFormats="[{key:'modal',label:'Export...'}]"
      @refresh="loadData"
      @export="showExportModal = true"
    />

    <div class="page-body">
      <div v-if="error" class="error-banner">{{ error }}</div>

      <div class="kpi-grid">
        <div class="kpi-card" :class="{ 'kpi-loading': summaryState === 'loading' }">
          <div class="kpi-icon kpi-icon-total">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
          </div>
          <span class="kpi-value">{{ summary.totalPolicies }}</span>
          <span class="kpi-label">Total Policies</span>
        </div>
        <div class="kpi-card" :class="{ 'kpi-loading': summaryState === 'loading' }">
          <div class="kpi-icon kpi-icon-enabled">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </div>
          <span class="kpi-value kpi-color-ok">{{ summary.enabledPolicies }}</span>
          <span class="kpi-label">Enabled</span>
        </div>
        <div class="kpi-card" :class="{ 'kpi-loading': summaryState === 'loading' }">
          <div class="kpi-icon kpi-icon-disabled">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/></svg>
          </div>
          <span class="kpi-value kpi-color-muted">{{ summary.disabledPolicies }}</span>
          <span class="kpi-label">Disabled</span>
        </div>
        <div class="kpi-card" :class="{ 'kpi-loading': summaryState === 'loading' }">
          <div class="kpi-icon kpi-icon-report">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
          </div>
          <span class="kpi-value kpi-color-warn">{{ summary.reportOnlyPolicies }}</span>
          <span class="kpi-label">Report-Only</span>
        </div>
        <div class="kpi-card" :class="{ 'kpi-loading': summaryState === 'loading' }">
          <div class="kpi-icon kpi-icon-location">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
          </div>
          <span class="kpi-value">{{ summary.namedLocations }}</span>
          <span class="kpi-label">Named Locations</span>
        </div>
        <div class="kpi-card" :class="{ 'kpi-loading': summaryState === 'loading' }">
          <div class="kpi-icon kpi-icon-trusted">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
          </div>
          <span class="kpi-value">{{ summary.trustedLocations }}</span>
          <span class="kpi-label">Trusted</span>
        </div>
      </div>

      <div class="ai-batch-bar">
        <button class="ai-batch-btn" @click="generateAllSummaries" :disabled="generatingAll || tableState === 'loading' || policies.length === 0">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="16" height="16">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.455L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.455L18 2.25l.259 1.036a3.375 3.375 0 002.455 2.455L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.455z"/>
          </svg>
          {{ generatingAll ? 'Generating ' + generateProgress + '/' + policies.length + '...' : 'Generate All AI Summaries' }}
        </button>
        <span v-if="generateAllResult" class="ai-batch-result">
          {{ generateAllResult.generated }} generated, {{ generateAllResult.cached }} cached, {{ generateAllResult.failed }} failed
        </span>
      </div>

      <div class="panels-row">
        <div class="panel-card">
          <div class="panel-header">
            <svg class="panel-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
            <div class="panel-title-group">
              <h3>Policies</h3>
              <span class="panel-count">{{ policies.length }} policies</span>
            </div>
          </div>

          <div v-if="tableState === 'loading'" class="loading-block">Loading policies...</div>
          <div v-else-if="tableState === 'empty'" class="empty-block">No Conditional Access policies are configured.</div>
          <div v-else class="policy-list">
            <div v-for="policy in policies" :key="policy.policyId" class="policy-row cursor-pointer" @click="openPolicy(policy)">
              <div class="policy-info">
                <span class="policy-title">{{ policy.displayName }}</span>
                <span class="policy-dates">Created {{ formatDate(policy.createdDateTime) }} &bull; Modified {{ formatDate(policy.modifiedDateTime) }}</span>
              </div>
              <div class="policy-meta">
                <span class="state-pill" :class="'pill-' + policy.state">
                  <span class="pill-dot"></span>
                  {{ policy.state === 'enabledForReportingButNotEnforced' ? 'Report-Only' : policy.state }}
                </span>
                <svg class="row-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="16" height="16">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-header">
            <svg class="panel-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <div class="panel-title-group">
              <h3>Named Locations</h3>
              <span class="panel-count">{{ locations.length }} locations</span>
            </div>
          </div>

          <div v-if="locationsState === 'loading'" class="loading-block">Loading locations...</div>
          <div v-else-if="locationsState === 'empty'" class="empty-block">No named locations are configured.</div>
          <div v-else class="location-list">
            <div v-for="loc in locations" :key="loc.locationId" class="location-row cursor-pointer" @click="openLocation(loc)">
              <div class="location-info">
                <span class="location-name">{{ loc.displayName }}</span>
                <span class="location-type">{{ formatLocationType(loc.locationType) }}</span>
              </div>
              <div class="location-meta">
                <span v-if="loc.isTrusted" class="trust-badge">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="12" height="12">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                  Trusted
                </span>
                <svg class="row-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="16" height="16">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showDrawer" class="drawer-overlay" @click.self="closeDrawer">
      <div class="drawer">
        <div class="drawer-header">
          <div class="drawer-title-group">
            <span class="state-pill drawer-state-pill" :class="'pill-' + selectedPolicy.state">
              <span class="pill-dot"></span>
              {{ selectedPolicy.state === 'enabledForReportingButNotEnforced' ? 'Report-Only' : selectedPolicy.state }}
            </span>
            <h2>{{ selectedPolicy.displayName }}</h2>
          </div>
          <button class="drawer-close" @click="closeDrawer">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="20" height="20">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="drawer-body">
          <div class="drawer-meta-grid">
            <div class="meta-item">
              <span class="meta-label">Policy ID</span>
              <span class="meta-value mono">{{ selectedPolicy.policyId }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Created</span>
              <span class="meta-value">{{ formatDate(selectedPolicy.createdDateTime) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Modified</span>
              <span class="meta-value">{{ formatDate(selectedPolicy.modifiedDateTime) }}</span>
            </div>
          </div>

          <div class="drawer-section" v-if="selectedPolicy.conditions">
            <div class="section-label">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"/>
              </svg>
              Conditions
            </div>
            <pre class="json-block">{{ JSON.stringify(selectedPolicy.conditions, null, 2) }}</pre>
          </div>

          <div class="drawer-section" v-if="selectedPolicy.grantControls">
            <div class="section-label">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
              </svg>
              Grant Controls
            </div>
            <pre class="json-block">{{ JSON.stringify(selectedPolicy.grantControls, null, 2) }}</pre>
          </div>

          <div class="drawer-section" v-if="selectedPolicy.sessionControls">
            <div class="section-label">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              Session Controls
            </div>
            <pre class="json-block">{{ JSON.stringify(selectedPolicy.sessionControls, null, 2) }}</pre>
          </div>

          <div class="drawer-section ai-section">
            <div class="section-label ai-label">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.455L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.455L18 2.25l.259 1.036a3.375 3.375 0 002.455 2.455L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.455z"/>
              </svg>
              AI Summary — Private Mind
            </div>
            <div v-if="aiLoading" class="ai-loading">
              <div class="ai-skeleton"></div>
              <span class="ai-loading-text">Private Mind AI is analyzing this policy...</span>
            </div>
            <div v-else-if="aiError" class="ai-error-block">
              <span class="ai-error-text">{{ aiError }}</span>
              <button class="ai-retry-btn" @click="loadAISummary(true)">Try Again</button>
            </div>
            <div v-else-if="aiSummary" class="ai-summary-content">
              <span v-if="aiCached" class="ai-cached-badge">Cached</span>
              <div class="ai-summary-body" v-html="aiSummary"></div>
              <button class="ai-retry-btn" @click="loadAISummary(true)">Regenerate</button>
            </div>
            <div v-else class="ai-empty">
              <p>No AI summary generated yet.</p>
              <button class="ai-generate-btn" @click="loadAISummary(true)">Generate Summary</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showLocationDrawer" class="drawer-overlay" @click.self="closeLocationDrawer">
      <div class="drawer">
        <div class="drawer-header">
          <div class="drawer-title-group">
            <span class="state-pill drawer-state-pill" :class="selectedLocation.isTrusted ? 'pill-enabled' : 'pill-disabled'">
              <span class="pill-dot"></span>
              {{ selectedLocation.isTrusted ? 'Trusted' : 'Not Trusted' }}
            </span>
            <h2>{{ selectedLocation.displayName }}</h2>
          </div>
          <button class="drawer-close" @click="closeLocationDrawer">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="20" height="20">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="drawer-body">
          <div class="drawer-meta-grid">
            <div class="meta-item">
              <span class="meta-label">Location ID</span>
              <span class="meta-value mono">{{ selectedLocation.locationId }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Type</span>
              <span class="meta-value">{{ formatLocationType(selectedLocation.locationType) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Trusted</span>
              <span class="meta-value">{{ selectedLocation.isTrusted ? 'Yes' : 'No' }}</span>
            </div>
          </div>

          <div class="drawer-section" v-if="selectedLocation.locationType === 'ipNamedLocation'">
            <div class="section-label">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5"/>
              </svg>
              IP Ranges
            </div>
            <div class="ip-range-list">
              <div v-for="(range, idx) in locationIpRanges" :key="idx" class="ip-range-row">
                <span class="ip-range-value">{{ range.cidrAddress || range.startAddress + ' - ' + range.endAddress || JSON.stringify(range) }}</span>
              </div>
              <div v-if="locationIpRanges.length === 0" class="ip-range-empty">No IP ranges configured.</div>
            </div>
          </div>

          <div class="drawer-section" v-if="selectedLocation.locationType === 'countryNamedLocation'">
            <div class="section-label">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5"/>
              </svg>
              Countries & Regions
            </div>
            <div class="country-list">
              <span v-for="country in locationCountries" :key="country" class="country-chip">{{ country }}</span>
              <span v-if="locationCountries.length === 0" class="ip-range-empty">No countries configured.</span>
            </div>
          </div>

          <div class="drawer-section">
            <div class="section-label">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m.75 0a1.5 1.5 0 1 0-3 0M7.5 6a1.5 1.5 0 1 1-3 0M3.75 12h9.75m-9.75 0a1.5 1.5 0 1 0-3 0m3 0a1.5 1.5 0 1 1-3 0m9.75 0a1.5 1.5 0 1 0-3 0m3 0a1.5 1.5 0 1 1-3 0m-4.5 6h9.75m-9.75 0a1.5 1.5 0 1 0-3 0m3 0a1.5 1.5 0 1 1-3 0"/>
              </svg>
              Raw Configuration
            </div>
            <pre class="json-block">{{ JSON.stringify(selectedLocation.raw, null, 2) }}</pre>
          </div>
        </div>
       </div>
     </div>

    <ExportModal
      :show="showExportModal"
      :policies="policies"
      :locations="locations"
      :exporting="exporting"
      @close="showExportModal = false"
      @export="handleExport"
    />
   </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import ExportModal from './ExportModal.vue';
import { getEntraConditionalAccessPolicies, getEntraNamedLocations, getEntraTenantSummary, getEntraTenantCapabilities, exportConditionalAccessDocument, getCaPolicySummary, generateCaPolicySummary } from '@/services/entra-api.js';
import { getCapabilityStateLabel } from '@/services/entra-utils.js';

export default {
  name: 'EntraConditionalAccess',
  components: { EntraPageHeader, ExportModal },
  data: function() {
    return {
      tenantId: '', tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '',
      freshnessState: 'fresh', lastCollection: '', dataCompleteness: null, capabilities: [],
      summary: { totalPolicies: 0, enabledPolicies: 0, disabledPolicies: 0, reportOnlyPolicies: 0, namedLocations: 0, trustedLocations: 0 },
      summaryState: 'loading',
      policies: [], tableState: 'loading',
      locations: [], locationsState: 'loading',
      error: '',
      showDrawer: false, selectedPolicy: {},
      exporting: false,
      showExportModal: false,
      aiLoading: false, aiSummary: '', aiError: '', aiCached: false,
      generatingAll: false, generateProgress: 0, generateAllResult: null,
      showLocationDrawer: false, selectedLocation: {}
    };
  },
  computed: {
    locationIpRanges: function() {
      if (!this.selectedLocation || !this.selectedLocation.raw) return [];
      var raw = this.selectedLocation.raw;
      return Array.isArray(raw.ipRanges) ? raw.ipRanges : [];
    },
    locationCountries: function() {
      if (!this.selectedLocation || !this.selectedLocation.raw) return [];
      var raw = this.selectedLocation.raw;
      return Array.isArray(raw.countriesAndRegions) ? raw.countriesAndRegions : [];
    }
  },
  methods: {
    formatDate: function(ts) {
      if (!ts) return '--';
      try { return new Date(ts).toLocaleDateString(); } catch(e) { return '--'; }
    },
    formatLocationType: function(type) {
      if (type === 'ipNamedLocation') return 'IP Range';
      if (type === 'countryNamedLocation') return 'Country';
      return type || '--';
    },
    loadData: function() {
      var self = this;
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) {
        this.error = 'Please select a tenant from the Portfolio page first.';
        this.tableState = 'empty'; this.locationsState = 'empty';
        return;
      }
      getEntraTenantSummary(this.tenantId).then(function(data) {
        var t = data.tenant || data;
        self.tenantName = t.displayName || ''; self.primaryDomain = t.primaryDomain || '';
        self.regionCode = t.regionCode || ''; self.tenantStatus = t.status || '';
        self.lastCollection = t.lastSuccessfulCollectionAt || ''; self.dataCompleteness = t.dataCompleteness || null;
      }).catch(function() {});
      getEntraTenantCapabilities(this.tenantId).then(function(data) {
        self.capabilities = (data || []).map(function(c) {
          return { key: c.capabilityKey, state: c.state, stateLabel: getCapabilityStateLabel(c.state) };
        });
      }).catch(function() {});
      getEntraConditionalAccessPolicies(this.tenantId).then(function(data) {
        var items = data.items || [];
        self.policies = items;
        self.summary.totalPolicies = items.length;
        self.summary.enabledPolicies = items.filter(function(p) { return p.state === 'enabled'; }).length;
        self.summary.disabledPolicies = items.filter(function(p) { return p.state === 'disabled'; }).length;
        self.summary.reportOnlyPolicies = items.filter(function(p) { return p.state === 'enabledForReportingButNotEnforced'; }).length;
        self.tableState = items.length === 0 ? 'empty' : 'loaded';
      }).catch(function(err) { self.error = err.message; self.tableState = 'error'; });
      getEntraNamedLocations(this.tenantId).then(function(data) {
        var items = data.items || [];
        self.locations = items;
        self.summary.namedLocations = items.length;
        self.summary.trustedLocations = items.filter(function(l) { return l.isTrusted; }).length;
        self.locationsState = items.length === 0 ? 'empty' : 'loaded';
        self.summaryState = 'loaded';
      }).catch(function(err) { self.error = err.message; self.locationsState = 'error'; });
    },
    openPolicy: function(policy) { this.selectedPolicy = policy; this.showDrawer = true; this.aiSummary = ''; this.aiError = ''; this.aiCached = false; this.loadAISummary(false); },
    closeDrawer: function() { this.showDrawer = false; this.selectedPolicy = {}; this.aiSummary = ''; this.aiError = ''; this.aiCached = false; },
    openLocation: function(loc) { this.selectedLocation = loc; this.showLocationDrawer = true; },
    closeLocationDrawer: function() { this.showLocationDrawer = false; this.selectedLocation = {}; },
    loadAISummary: function(force) {
      var self = this;
      if (!self.selectedPolicy.policyId) return;
      self.aiLoading = true;
      self.aiError = '';
      var apiCall = force
        ? generateCaPolicySummary(self.tenantId, self.selectedPolicy.policyId)
        : getCaPolicySummary(self.tenantId, self.selectedPolicy.policyId);
      apiCall.then(function(data) {
        if (data && data.summary) {
          self.aiSummary = data.summary;
          self.aiCached = !!data.cached;
        } else {
          self.aiSummary = '';
          self.aiCached = false;
        }
      }).catch(function(err) {
        self.aiError = err.message || 'Failed to load AI summary';
      }).finally(function() {
        self.aiLoading = false;
      });
    },
    generateAllSummaries: function() {
      var self = this;
      if (self.generatingAll) return;
      self.generatingAll = true;
      self.generateProgress = 0;
      self.generateAllResult = null;
      self.error = '';
      var generated = 0, cached = 0, failed = 0;
      function processNext(idx) {
        if (idx >= self.policies.length) {
          self.generatingAll = false;
          self.generateAllResult = { generated: generated, cached: cached, failed: failed };
          return;
        }
        var policy = self.policies[idx];
        self.generateProgress = idx + 1;
        generateCaPolicySummary(self.tenantId, policy.policyId).then(function(data) {
          if (data.cached) { cached++; } else { generated++; }
        }).catch(function() {
          failed++;
        }).then(function() {
          processNext(idx + 1);
        });
      }
      processNext(0);
    },
    handleExport: function(options) {
      var self = this;
      if (self.exporting) return;
      self.exporting = true;
      self.error = '';
      var format = options.format || 'pdf';
      exportConditionalAccessDocument(self.tenantId, options).then(function(blob) {
        var dateStr = new Date().toISOString().slice(0, 10);
        var safeName = (self.tenantName || 'tenant').replace(/[^a-zA-Z0-9]/g, '_');
        var ext = format === 'pdf' ? 'pdf' : format === 'csv' ? 'csv' : 'json';
        var suffix = options.policyIds && options.policyIds.length > 0 ? '_Selected' : '';
        var a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = safeName + '_Conditional_Access' + suffix + '_' + dateStr + '.' + ext;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
        self.showExportModal = false;
      }).catch(function(err) {
        self.error = 'Export failed: ' + (err.message || 'Unknown error');
      }).finally(function() {
        self.exporting = false;
      });
    }
  },
  mounted: function() { this.loadData(); },
  watch: { '$route.params.tenantId': 'loadData' }
};
</script>

<style scoped>
.entra-page { min-height: 100%; background: #F0F9FF; }
.page-body { padding: 20px 24px; }

.error-banner { padding: 12px 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; color: #991b1b; font-size: 0.8125rem; margin-bottom: 20px; }

.kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 10px; margin-bottom: 20px; }

.kpi-card {
  background: #ffffff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px;
  display: flex; flex-direction: column; gap: 4px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.kpi-card:hover { border-color: #7dd3fc; box-shadow: 0 2px 8px rgba(3,105,161,0.06); }

.kpi-icon { width: 28px; height: 28px; border-radius: 7px; display: flex; align-items: center; justify-content: center; margin-bottom: 4px; }
.kpi-icon svg { width: 16px; height: 16px; }
.kpi-icon-total { background: #dbeafe; color: #0369A1; }
.kpi-icon-enabled { background: #dcfce7; color: #047857; }
.kpi-icon-disabled { background: #f1f5f9; color: #64748b; }
.kpi-icon-report { background: #fef3c7; color: #92400e; }
.kpi-icon-location { background: #ede9fe; color: #6d28d9; }
.kpi-icon-trusted { background: #dcfce7; color: #047857; }

.kpi-value { font-size: 1.375rem; font-weight: 700; color: #0C4A6E; line-height: 1; }
.kpi-color-ok { color: #047857; }
.kpi-color-warn { color: #92400e; }
.kpi-color-muted { color: #64748b; }
.kpi-label { font-size: 0.6875rem; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.03em; }

.kpi-loading .kpi-value { color: #cbd5e1; }

.panels-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

.panel-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }

.panel-header { display: flex; align-items: center; gap: 10px; padding: 14px 16px; border-bottom: 1px solid #f1f5f9; }
.panel-icon { width: 20px; height: 20px; color: #0369A1; flex-shrink: 0; }
.panel-title-group { display: flex; flex-direction: column; gap: 1px; flex: 1; }
.panel-title-group h3 { margin: 0; font-size: 0.8125rem; font-weight: 600; color: #0C4A6E; }
.panel-count { font-size: 0.6875rem; color: #94a3b8; }

.loading-block { padding: 32px; text-align: center; color: #94a3b8; font-size: 0.8125rem; }
.empty-block { padding: 32px; text-align: center; color: #94a3b8; font-size: 0.8125rem; }

.policy-list { display: flex; flex-direction: column; }
.policy-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; border-bottom: 1px solid #f8fafc;
  transition: background 0.1s; cursor: pointer;
}
.policy-row:last-child { border-bottom: none; }
.policy-row:hover { background: #F0F9FF; }

.policy-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; margin-right: 12px; }
.policy-title { font-size: 0.8125rem; font-weight: 600; color: #0C4A6E; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.policy-dates { font-size: 0.6875rem; color: #94a3b8; }

.policy-meta { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

.state-pill {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.625rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  padding: 3px 10px; border-radius: 12px; white-space: nowrap;
}
.pill-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.pill-enabled { background: #dcfce7; color: #047857; }
.pill-enabled .pill-dot { background: #047857; }
.pill-disabled { background: #f1f5f9; color: #64748b; }
.pill-disabled .pill-dot { background: #94a3b8; }
.pill-enabledForReportingButNotEnforced { background: #fef3c7; color: #92400e; }
.pill-enabledForReportingButNotEnforced .pill-dot { background: #92400e; }

.row-chevron { color: #cbd5e1; flex-shrink: 0; transition: transform 0.15s; }
.policy-row:hover .row-chevron { transform: translateX(2px); color: #0369A1; }

.location-list { display: flex; flex-direction: column; }
.location-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid #f8fafc; }
.location-row:last-child { border-bottom: none; }
.location-row:hover { background: #F0F9FF; }

.location-info { display: flex; flex-direction: column; gap: 2px; }
.location-name { font-size: 0.8125rem; font-weight: 600; color: #0C4A6E; }
.location-type { font-size: 0.6875rem; color: #94a3b8; }

.trust-badge {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.625rem; font-weight: 600; color: #047857;
  background: #dcfce7; padding: 2px 8px; border-radius: 10px;
}

.drawer-overlay { position: fixed; top: 0; right: 0; bottom: 0; left: 0; background: rgba(12,74,110,0.4); z-index: 100; display: flex; justify-content: flex-end; backdrop-filter: blur(2px); }
.drawer { width: 640px; max-width: 92vw; height: 100%; background: #ffffff; overflow-y: auto; box-shadow: -12px 0 40px rgba(3,105,161,0.12); animation: drawer-slide 0.22s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes drawer-slide { from { transform: translateX(40px); opacity: 0.7; } to { transform: translateX(0); opacity: 1; } }

.drawer-header { display: flex; justify-content: space-between; align-items: flex-start; padding: 16px 20px; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0; background: rgba(255,255,255,0.95); backdrop-filter: blur(8px); z-index: 1; }
.drawer-title-group { display: flex; flex-direction: column; gap: 8px; }
.drawer-state-pill { font-size: 0.6875rem; padding: 3px 12px; }
.drawer-header h2 { margin: 0; font-size: 1rem; font-weight: 600; color: #0C4A6E; word-break: break-word; }
.drawer-close { background: none; border: none; color: #94a3b8; cursor: pointer; padding: 6px; border-radius: 8px; transition: color 0.15s, background 0.15s; display: flex; flex-shrink: 0; }
.drawer-close:hover { color: #475569; background: #f1f5f9; }

.drawer-body { padding: 20px; }

.drawer-meta-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid #f1f5f9; }
.meta-item { display: flex; flex-direction: column; gap: 3px; }
.meta-label { font-size: 0.625rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; }
.meta-value { font-size: 0.8125rem; color: #0C4A6E; }
.meta-value.mono { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 0.6875rem; word-break: break-all; }

.drawer-section { margin-bottom: 20px; }
.drawer-section:last-child { margin-bottom: 0; }

.section-label {
  display: flex; align-items: center; gap: 8px;
  font-size: 0.75rem; font-weight: 700; color: #0369A1;
  text-transform: uppercase; letter-spacing: 0.04em;
  margin-bottom: 8px;
}
.section-label svg { color: #7dd3fc; }

.json-block {
  background: #F0F9FF; border: 1px solid #bae6fd; border-radius: 8px;
  padding: 14px; font-size: 0.6875rem; color: #0C4A6E;
  overflow-x: auto; white-space: pre-wrap;
  font-family: 'SF Mono', 'Fira Code', monospace; line-height: 1.5;
}

@media (max-width: 768px) {
  .panels-row { grid-template-columns: 1fr; }
  .drawer-meta-grid { grid-template-columns: 1fr 1fr; }
}

.ai-batch-bar { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
.ai-batch-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: #13173F; color: #fff; border: none; padding: 7px 16px;
  border-radius: 8px; font-size: 0.8125rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s; font-family: 'Inter', sans-serif;
}
.ai-batch-btn:hover:not(:disabled) { background: #1F243A; }
.ai-batch-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ai-batch-btn svg { color: #35B35E; }
.ai-batch-result { font-size: 0.75rem; color: #64748b; font-weight: 500; }

.ai-section { margin-top: 8px; }
.ai-label { color: #13173F; }
.ai-loading { display: flex; flex-direction: column; gap: 10px; padding: 16px; background: #f8fafc; border-radius: 8px; }
.ai-skeleton { height: 60px; background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%); background-size: 200% 100%; border-radius: 6px; animation: ai-shimmer 1.5s infinite; }
@keyframes ai-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.ai-loading-text { font-size: 0.75rem; color: #64748b; font-weight: 500; }
.ai-error-block { padding: 12px 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; display: flex; flex-direction: column; gap: 8px; }
.ai-error-text { font-size: 0.75rem; color: #991b1b; }
.ai-retry-btn { align-self: flex-start; background: #0369A1; color: #fff; border: none; padding: 4px 12px; border-radius: 6px; font-size: 0.75rem; font-weight: 500; cursor: pointer; }
.ai-retry-btn:hover { background: #075985; }
.ai-summary-content { padding: 16px 18px; background: #f0f9ff; border: 1px solid #bae6fd; border-left: 3px solid #35B35E; border-radius: 8px; }
.ai-cached-badge { display: inline-block; background: #dcfce7; color: #047857; font-size: 0.625rem; font-weight: 700; padding: 2px 8px; border-radius: 10px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }
.ai-summary-body { font-size: 0.8125rem; color: #0C4A6E; line-height: 1.6; }
.ai-summary-body :deep(h2) { font-size: 1rem; margin: 0 0 8px; color: #0C4A6E; font-weight: 700; }
.ai-summary-body :deep(h3) { font-size: 0.875rem; margin: 12px 0 5px; color: #13173F; font-weight: 600; padding-top: 6px; border-top: 1px solid #e2e8f0; }
.ai-summary-body :deep(h3:first-child) { border-top: none; padding-top: 0; }
.ai-summary-body :deep(h4) { font-size: 0.8125rem; margin: 8px 0 4px; color: #37B25E; font-weight: 600; }
.ai-summary-body :deep(p) { margin: 0 0 8px; }
.ai-summary-body :deep(p:last-child) { margin-bottom: 0; }
.ai-summary-body :deep(ul) { margin: 0 0 8px; padding-left: 20px; }
.ai-summary-body :deep(ol) { margin: 0 0 8px; padding-left: 20px; }
.ai-summary-body :deep(li) { margin-bottom: 5px; line-height: 1.5; }
.ai-summary-body :deep(li:last-child) { margin-bottom: 0; }
.ai-summary-body :deep(code) { background: #e0e8f0; padding: 1px 5px; border-radius: 3px; font-size: 0.75rem; color: #37B25E; }
.ai-summary-body :deep(strong) { color: #13173F; font-weight: 600; }
.ai-empty { padding: 16px; text-align: center; }
.ai-empty p { font-size: 0.75rem; color: #94a3b8; margin-bottom: 10px; }
.ai-generate-btn { background: #35B35E; color: #fff; border: none; padding: 6px 16px; border-radius: 6px; font-size: 0.75rem; font-weight: 600; cursor: pointer; }
.ai-generate-btn:hover { background: #2da34f; }

.ip-range-list { display: flex; flex-direction: column; gap: 4px; }
.ip-range-row { background: #F0F9FF; border: 1px solid #bae6fd; border-radius: 6px; padding: 8px 12px; }
.ip-range-value { font-size: 0.75rem; color: #0C4A6E; font-family: 'SF Mono', 'Fira Code', monospace; }
.ip-range-empty { font-size: 0.8125rem; color: #94a3b8; padding: 12px; text-align: center; }
.country-list { display: flex; flex-wrap: wrap; gap: 6px; }
.country-chip { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 6px; padding: 4px 10px; font-size: 0.75rem; font-weight: 600; color: #0C4A6E; }
</style>
